/**
 * useTextSelection Hook
 * Detects text selection on PDF pages and provides selection info.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import type { Position } from '../types';

interface TextSelection {
  /**
   * The selected text content
   */
  text: string;

  /**
   * The page number where the selection occurred
   */
  pageNumber: number;

  /**
   * The normalized position (0-1) of the selection on the page
   */
  position: Position;

  /**
   * The bounding client rect of the selection (for positioning UI)
   */
  rect: DOMRect;
}

interface UseTextSelectionReturn {
  /**
   * Current text selection, or null if nothing is selected
   */
  selection: TextSelection | null;

  /**
   * Clear the current selection
   */
  clearSelection: () => void;

  /**
   * Whether selection detection is active
   */
  isActive: boolean;

  /**
   * Enable/disable selection detection
   */
  setActive: (active: boolean) => void;
}

/**
 * Hook for detecting text selection on PDF pages.
 *
 * Usage:
 * ```tsx
 * const { selection, clearSelection } = useTextSelection();
 *
 * useEffect(() => {
 *   if (selection) {
 *     console.log('Selected:', selection.text);
 *   }
 * }, [selection]);
 * ```
 */
export function useTextSelection(): UseTextSelectionReturn {
  const [selection, setSelection] = useState<TextSelection | null>(null);
  const [isActive, setActive] = useState(true);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const clearSelection = useCallback(() => {
    setSelection(null);
  }, []);

  const handleSelectionChange = useCallback(() => {
    if (!isActive) return;

    // Debounce to avoid too many updates
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      const windowSelection = window.getSelection();

      if (!windowSelection || windowSelection.isCollapsed) {
        // No selection or cursor only - clear any existing selection
        setSelection(null);
        return;
      }

      const selectedText = windowSelection.toString().trim();
      if (!selectedText) {
        setSelection(null);
        return;
      }

      // Get the selection range
      const range = windowSelection.getRangeAt(0);
      const rect = range.getBoundingClientRect();

      // Find the PDF page container
      const pageElement = findPDFPageElement(range.commonAncestorContainer);
      if (!pageElement) {
        // Selection is not within a PDF page
        return;
      }

      // Extract page number from the page element
      const pageNumber = extractPageNumber(pageElement);
      if (pageNumber === null) {
        return;
      }

      // Get page dimensions and calculate normalized position
      const pageRect = pageElement.getBoundingClientRect();
      const position = calculateNormalizedPosition(rect, pageRect);

      setSelection({
        text: selectedText,
        pageNumber,
        position,
        rect,
      });
    }, 200);
  }, [isActive]);

  // Handle mouseup event for selection
  const handleMouseUp = useCallback(() => {
    // Small delay to allow selection to complete
    setTimeout(handleSelectionChange, 10);
  }, [handleSelectionChange]);

  useEffect(() => {
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('selectionchange', handleSelectionChange);

    return () => {
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('selectionchange', handleSelectionChange);

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [handleMouseUp, handleSelectionChange]);

  return {
    selection,
    clearSelection,
    isActive,
    setActive,
  };
}

/**
 * Find the PDF page element containing the given node.
 */
function findPDFPageElement(node: Node | null): HTMLElement | null {
  let current = node;

  while (current) {
    if (current instanceof HTMLElement) {
      // Check for PDF page identifiers
      // The PDFViewer component uses id="pdf-page-{number}"
      if (current.id && current.id.startsWith('pdf-page-')) {
        return current;
      }

      // Also check for react-pdf page class
      if (current.classList.contains('react-pdf__Page')) {
        return current;
      }

      // Check data attribute
      if (current.dataset.pageNumber) {
        return current;
      }
    }

    current = current.parentNode;
  }

  return null;
}

/**
 * Extract page number from a PDF page element.
 */
function extractPageNumber(element: HTMLElement): number | null {
  // Try id format: pdf-page-{number}
  if (element.id && element.id.startsWith('pdf-page-')) {
    const num = parseInt(element.id.replace('pdf-page-', ''), 10);
    if (!isNaN(num)) {
      return num;
    }
  }

  // Try data attribute
  if (element.dataset.pageNumber) {
    const num = parseInt(element.dataset.pageNumber, 10);
    if (!isNaN(num)) {
      return num;
    }
  }

  // Try react-pdf's data-page-number attribute
  const pageAttr = element.getAttribute('data-page-number');
  if (pageAttr) {
    const num = parseInt(pageAttr, 10);
    if (!isNaN(num)) {
      return num;
    }
  }

  return null;
}

/**
 * Calculate normalized position (0-1) from selection rect and page rect.
 */
function calculateNormalizedPosition(
  selectionRect: DOMRect,
  pageRect: DOMRect
): Position {
  const x = (selectionRect.left - pageRect.left) / pageRect.width;
  const y = (selectionRect.top - pageRect.top) / pageRect.height;
  const width = selectionRect.width / pageRect.width;
  const height = selectionRect.height / pageRect.height;

  // Clamp values to 0-1 range
  return {
    x: Math.max(0, Math.min(1, x)),
    y: Math.max(0, Math.min(1, y)),
    width: Math.max(0, Math.min(1, width)),
    height: Math.max(0, Math.min(1, height)),
  };
}

export default useTextSelection;
